import { Download, ExternalLink, X } from "lucide-react";
import { useCallback, useEffect } from "react";

interface ImageLightboxProps {
  src: string;
  alt: string;
  onClose: () => void;
}

/**
 * Full-screen overlay lightbox for viewing images at full resolution.
 * Closes on Escape, click-outside (backdrop), or the X button.
 * Includes download and open-in-new-tab actions.
 */
export function ImageLightbox({ src, alt, onClose }: Readonly<ImageLightboxProps>) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    // Prevent background scroll while lightbox is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prev;
    };
  }, [handleKeyDown]);

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = src;
    a.download = alt || "image";
    a.click();
  };

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
      {/* Backdrop — click to dismiss */}
      <button
        type="button"
        className="absolute inset-0 bg-black/80 backdrop-blur-xs border-none cursor-default w-full h-full"
        aria-label="Close lightbox"
        onClick={onClose}
      />

      {/* Content */}
      <div className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-3">
        {/* Top-right actions */}
        <div className="absolute -top-2 -right-2 z-10 flex items-center gap-1">
          <button
            onClick={handleDownload}
            className="p-2 bg-surface-secondary hover:bg-interactive-active rounded-full text-content-secondary hover:text-content-primary transition-colors"
            title="Download image"
          >
            <Download className="w-4 h-4" />
          </button>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 bg-surface-secondary hover:bg-interactive-active rounded-full text-content-secondary hover:text-content-primary transition-colors"
            title="Open in new tab"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
          <button
            onClick={onClose}
            className="p-2 bg-surface-secondary hover:bg-interactive-active rounded-full text-content-secondary hover:text-content-primary transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Image */}
        <img
          src={src}
          alt={alt}
          className="max-h-[85vh] max-w-[90vw] rounded-lg border border-border-secondary object-contain"
        />

        {/* Caption */}
        {alt && (
          <div className="text-sm text-content-tertiary bg-surface-secondary/80 px-3 py-1.5 rounded-lg max-w-lg truncate">
            {alt}
          </div>
        )}
      </div>
    </div>
  );
}
